import {gql} from '@apollo/client';
import {toRelayId, useMutation$} from 'graphqlsupport';
import {Offer} from 'offer';
import {useCallback} from 'react';
import {useDomainErrorDialog} from '../../common/component/ErrorDialog';
import {sanitizeOfferInput, OfferFormInput} from '../component/OfferForm';
import {OfferContentFragment, StatusAndSubmitter} from '../domain/graphql';

type EditOfferMutationInput = {
    offerGuid: string;
    offerInput: OfferFormInput;
};
type EditOfferMutationResult = {
    editOffer: Partial<Offer>;
}
const EDIT_OFFER_MUTATION = gql`
mutation ProviderEditOffer(
  $offerGuid: ID!, 
  $offerInput: OfferContentInput
) {
  editOffer(
    id: $offerGuid
    payload: $offerInput
  ) {
    id
    guid
    ...${OfferContentFragment.name}
    ...${StatusAndSubmitter.name}
    referral {
        id
        guid
        caseNumber
    }
  }
}
${OfferContentFragment.value}
${StatusAndSubmitter.value}
`;
export const useEditOffer$ = (
    offerGuid: string,
    successCallback: (updatedOffer: Partial<Offer>) => any,
) => {
    const {
        isRunning$,
        execute,
        data,
    } = useMutation$<EditOfferMutationInput, EditOfferMutationResult>(EDIT_OFFER_MUTATION);

    const {errorMessage$, tryParsingDomainError} = useDomainErrorDialog();

    const editOffer = useCallback(async (content: OfferFormInput) => {
        try {
            const {data: result} = await execute({
                variables: {
                    offerGuid: toRelayId(offerGuid, 'Offer'),
                    offerInput: sanitizeOfferInput(content),
                }
            });
            if (!!result) {
                successCallback(result.editOffer);
            }
        } catch (e) {
            if (!tryParsingDomainError(e)) {
                throw e;
            }
        }
    }, [execute, tryParsingDomainError, offerGuid, successCallback]);

    return {
        isRunning$,
        execute: editOffer,
        data,
        errorMessage$,
    };
};
